import React, { Suspense } from 'react';
import { ArrowDown } from 'lucide-react';
import type {
  Message,
  ToolCall,
  SupportRecord,
  ProductRecord,
  BubbleOption,
  ProductAction,
  ProductCard,
} from '../types';
import MessageBubble from './messages/MessageBubble';
import ActiveToolCalls from './messages/ActiveToolCalls';
import StreamingMessage from './messages/StreamingMessage';
import TypingIndicator from './messages/TypingIndicator';
import ErrorBoundary from './ErrorBoundary';
import { isSafeUrl } from '../utils/urlSafety';
import ButtonsMessage from './rich/ButtonsMessage';
import { useChatConfig } from '../context/ChatContext';
import { normalizeActionValue } from '../utils/actionFilters';
import type { WidgetStrings } from '../utils/i18n';
import { DEFAULT_WIDGET_STRINGS } from '../utils/i18n';

const RecordView = React.lazy(() => import('./records/RecordView'));

interface MainContentProps {
  view: string;
  viewMode?: string;
  onModeChange: (viewKey: string, modeKey: string) => void;
  records: Record<string, unknown[]>;
  messages: Message[];
  isTyping: boolean;
  error: string;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  messagesContainerRef?: React.RefObject<HTMLDivElement | null>;
  onMessagesScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  streamingMessage: Message | null;
  activeToolCalls: ToolCall[];
  onButtonClick?: (value: string) => void;
  onProductAction?: (payload: { action: ProductAction; product: ProductCard }) => void;
  onOrderAction?: (payload: {
    action: { label: string; value?: string; href?: string; target?: '_self' | '_blank' };
    orderId?: string | null;
    orderName?: string | null;
  }) => void;
  onFormSubmit?: (formId: string | undefined, data: Record<string, string>) => void;
  emptyStateMessage?: string;
  bubbleOptions?: BubbleOption[];
  suggestedActions?: string[];
  onSuggestedAction?: (value: string) => void;
  panelId: string;
  labelledBy: string;
  showScrollToBottom?: boolean;
  newMessagesCount?: number;
  onScrollToBottom?: () => void;
  strings?: WidgetStrings;
  direction?: 'ltr' | 'rtl';
}

const MainContent = React.memo(function MainContent({
  view,
  viewMode,
  onModeChange,
  records,
  messages,
  isTyping,
  error,
  messagesEndRef,
  messagesContainerRef,
  onMessagesScroll,
  streamingMessage,
  activeToolCalls,
  onButtonClick,
  onProductAction,
  onOrderAction,
  onFormSubmit,
  emptyStateMessage,
  bubbleOptions,
  suggestedActions,
  onSuggestedAction,
  panelId,
  labelledBy,
  showScrollToBottom,
  newMessagesCount,
  onScrollToBottom,
  strings = DEFAULT_WIDGET_STRINGS,
  direction = 'ltr',
}: MainContentProps) {
  const { trackAnalytics } = useChatConfig();

  if (view !== 'chat') {
    return (
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={labelledBy}
        tabIndex={0}
        className="flex-1 flex flex-col overflow-hidden min-h-0 focus:outline-2 focus:outline-blue-500 relative"
      >
        <ErrorBoundary>
          <Suspense
            fallback={
              <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                {strings.loading}
              </div>
            }
          >
            <RecordView
              type={view}
              records={(records?.[view] as (SupportRecord | ProductRecord)[]) || []}
              mode={viewMode}
              onModeChange={onModeChange}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    );
  }

  return (
    <div
      id={panelId}
      role="tabpanel"
      aria-labelledby={labelledBy}
      tabIndex={0}
      className="flex-1 flex flex-col overflow-hidden min-h-0 focus:outline-2 focus:outline-blue-500 relative"
    >
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-3 space-y-2 min-h-0 chat-scroll rw-chat-surface"
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label={strings.chatMessages}
        aria-busy={isTyping || !!streamingMessage}
        ref={messagesContainerRef}
        onScroll={onMessagesScroll}
      >
        {messages.length === 0 && !streamingMessage && (
          <div className="h-full flex flex-col items-center justify-center text-center text-sm text-gray-500">
            {emptyStateMessage || strings.emptyState}
            {bubbleOptions && bubbleOptions.length > 0 ? (
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {bubbleOptions.map((option, index) => {
                  const isString = typeof option === 'string';
                  const label = isString ? option : option.label;
                  const value = isString ? option : option.value || option.label;
                  const href = !isString ? option.href : undefined;
                  const target = !isString ? option.target : undefined;
                  const normalizedLabel = normalizeActionValue(label);
                  const normalizedValue = normalizeActionValue(String(value || ''));
                  const isReturnExchange =
                    normalizedLabel === 'return exchange' ||
                    normalizedLabel === 'return or exchange' ||
                    normalizedValue === 'return exchange' ||
                    normalizedValue === 'return or exchange';
                  const actionValue = isReturnExchange
                    ? '__return_exchange__'
                    : typeof value === 'string'
                      ? value.trim()
                      : String(value || '').trim();
                  const key = `${label}-${index}`;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        const safeHref = href && isSafeUrl(href) ? href : '';
                        trackAnalytics('bubble_option_click', {
                          label,
                          value: actionValue,
                          source: 'main_empty',
                          hasHref: Boolean(safeHref),
                          target: target || null,
                        });
                        if (safeHref && typeof window !== 'undefined') {
                          if (target === '_self') {
                            try {
                              window.open(safeHref, '_self');
                            } catch {
                              try {
                                window.location.assign(safeHref);
                              } catch {
                                // Ignore if navigation is blocked.
                              }
                            }
                          } else {
                            window.open(safeHref, '_blank', 'noopener,noreferrer');
                          }
                          if (!isReturnExchange) {
                            return;
                          }
                        }
                        if (isReturnExchange) onSuggestedAction?.('__return_exchange__');
                        else if (actionValue) onSuggestedAction?.(actionValue);
                      }}
                      className="px-4 py-2 rounded-full border border-gray-200 bg-white text-gray-700 text-sm font-medium shadow-sm hover:bg-gray-100 focus:outline-2 focus:outline-blue-500"
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : null}
            {!bubbleOptions || bubbleOptions.length === 0
              ? suggestedActions &&
                suggestedActions.length > 0 && (
                  <div className="mt-3 flex justify-center">
                    <ButtonsMessage
                      content={{
                        buttons: suggestedActions.map(action => ({ label: action, value: action })),
                      }}
                      onButtonClick={onSuggestedAction}
                      trackAnalytics={trackAnalytics}
                    />
                  </div>
                )
              : null}
          </div>
        )}
        {messages.map(m => (
          <MessageBubble
            key={m.id}
            message={m}
            onButtonClick={onButtonClick}
            onProductAction={onProductAction}
            onOrderAction={onOrderAction}
            onFormSubmit={onFormSubmit}
            strings={strings}
            direction={direction}
          />
        ))}
        <ActiveToolCalls toolCalls={activeToolCalls} strings={strings} />
        {streamingMessage && <StreamingMessage message={streamingMessage} />}
        {isTyping && !streamingMessage && <TypingIndicator strings={strings} />}
        {error && (
          <div className="text-xs text-red-600" role="alert">
            {error}
          </div>
        )}
        <div aria-hidden="true" id="messages-end" ref={messagesEndRef} />
      </div>
      {showScrollToBottom && (
        <button
          type="button"
          onClick={() => onScrollToBottom?.()}
          className={`absolute bottom-4 ${direction === 'rtl' ? 'left-4' : 'right-4'} flex items-center gap-2 px-3 py-2 rounded-full border border-gray-200 bg-white text-gray-700 shadow hover:bg-gray-100 focus:outline-2 focus:outline-blue-500`}
          aria-label={strings.scrollToLatest}
        >
          <ArrowDown className="w-4 h-4" aria-hidden="true" />
          <span className="text-xs font-semibold">{strings.latest}</span>
          {typeof newMessagesCount === 'number' && newMessagesCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-600 text-white text-xs font-semibold">
              {newMessagesCount > 99 ? '99+' : String(newMessagesCount)}
            </span>
          )}
        </button>
      )}
    </div>
  );
});

export default MainContent;
